import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

// MUI renders a textarea whenever TextField is multiline. Keeping the concrete element type here
// also prevents a ref intended for a hidden input from being wired to the wrong DOM node.
export type ChatInputElement = HTMLTextAreaElement

interface ChatInputFocusRefs {
  inlineInputRef: RefObject<ChatInputElement>
  dialogInputRef: RefObject<ChatInputElement>
  inlineSendButtonRef: RefObject<HTMLButtonElement>
  dialogSendButtonRef: RefObject<HTMLButtonElement>
}

/**
 * Restores the cursor to the currently visible chat input after a turn finishes.
 *
 * Both inputs stay mounted while the full-screen dialog is open. A completion counter is used
 * instead of the streaming boolean because React may batch a very fast true -> false transition.
 * It also lets the CURRENT dialog/accordion state choose a visible target.
 */
export const useChatInputFocusAfterTurn = (
  completedTurnCount: number,
  dialogOpen: boolean,
  inlineInputVisible: boolean,
): ChatInputFocusRefs => {
  const inlineInputRef = useRef<ChatInputElement>(null)
  const dialogInputRef = useRef<ChatInputElement>(null)
  const inlineSendButtonRef = useRef<HTMLButtonElement>(null)
  const dialogSendButtonRef = useRef<HTMLButtonElement>(null)
  const handledCompletedTurnCountRef = useRef(completedTurnCount)

  useEffect(() => {
    if (handledCompletedTurnCountRef.current === completedTurnCount) return

    handledCompletedTurnCountRef.current = completedTurnCount
    const activeInput = dialogOpen
      ? dialogInputRef.current
      : inlineInputVisible
        ? inlineInputRef.current
        : null
    const activeElement = document.activeElement
    const focusStayedInChat = !activeElement
      || activeElement === document.body
      || activeElement === inlineInputRef.current
      || activeElement === dialogInputRef.current
      || activeElement === inlineSendButtonRef.current
      || activeElement === dialogSendButtonRef.current
      // Disabling the textbox while streaming can make MUI's focus trap fall back to its Drawer or
      // Dialog paper (tabindex=-1). That is framework bookkeeping, not a user choosing another
      // control, so return from this containing fallback just like from the send button.
      || (!!activeInput
        && activeElement instanceof HTMLElement
        && activeElement.tabIndex === -1
        && activeElement.contains(activeInput))
    // Reporting a problem, editing another field, or opening another dialog while Max answers is
    // an intentional focus move. Completion must not pull the keyboard away from that action.
    if (!focusStayedInChat) return
    activeInput?.focus({ preventScroll: true })
  }, [completedTurnCount, dialogOpen, inlineInputVisible])

  return { inlineInputRef, dialogInputRef, inlineSendButtonRef, dialogSendButtonRef }
}
